import { SUPPORT_EMAIL } from "@/components/PublicLayout";

const LAST_UPDATED = "February 11, 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24" data-testid={`terms-section-${id}`}>
    <h2 className="font-display text-xl sm:text-2xl mb-3 text-white">{title}</h2>
    <div className="text-white/70 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-xs uppercase tracking-wider text-cyan-300/80 mb-3">
        Legal
      </div>
      <h1
        className="font-display text-3xl sm:text-4xl tracking-tight mb-3"
        data-testid="terms-heading"
      >
        Terms of Service
      </h1>
      <p className="text-white/45 text-sm mb-10">Last updated: {LAST_UPDATED}</p>

      <Section id="agreement" title="Agreement">
        <p>
          By accessing or using Journey ("the Service"), you agree to these Terms of
          Service. If you do not agree, do not use the Service. For questions, contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section id="service" title="Description of the Service">
        <p>
          Journey is a private, single-user AI assistant providing conversational
          memory, reminders, and delegated SMS task follow-ups on behalf of its
          account owner. The Service is operational and transactional in nature and
          is not a marketing, advertising, or bulk-messaging platform.
        </p>
      </Section>

      <Section id="account" title="Account &amp; eligibility">
        <p>
          The Service is intended for use by the account owner who provisioned it.
          You must be at least 18 years old (or the legal age of majority in your
          jurisdiction) to use the Service. You are responsible for safeguarding
          access to the app and any devices that have it installed.
        </p>
      </Section>

      <Section id="acceptable-use" title="Acceptable use">
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Send unsolicited, harassing, deceptive, or unlawful messages of any kind.</li>
          <li>
            Contact any individual via SMS without an existing relationship or prior
            consent — you alone are responsible for ensuring you have the right to
            message every phone number you add as a contact.
          </li>
          <li>
            Send marketing, promotional, political, or bulk SMS content. Journey's SMS
            integration is for transactional follow-ups only.
          </li>
          <li>
            Submit content that infringes intellectual property, violates privacy, or
            facilitates illegal activity.
          </li>
          <li>
            Attempt to reverse-engineer, abuse rate limits, or interfere with the
            normal operation of the Service or its underlying infrastructure.
          </li>
        </ul>
        <p>
          Violation of these rules may result in immediate suspension of the Service
          and reporting to the relevant carriers, providers, or authorities.
        </p>
      </Section>

      <Section id="messaging" title="SMS messaging terms">
        <p>
          SMS recipients may reply <code className="text-cyan-200">STOP</code>,{" "}
          <code className="text-cyan-200">UNSUBSCRIBE</code>, or{" "}
          <code className="text-cyan-200">END</code> at any time to opt out of further
          messages. Replying <code className="text-cyan-200">HELP</code> returns
          support contact information. Standard message-and-data rates from the
          recipient's carrier may apply.
        </p>
        <p>
          The account owner is solely responsible for the content of messages sent
          via the Service and for compliance with all applicable laws, carrier rules,
          and CTIA / TCPA / 10DLC guidelines.
        </p>
      </Section>

      <Section id="content" title="Your content">
        <p>
          You retain ownership of all content you create or upload to the Service
          (conversations, memories, reminders, contacts, tasks, files). You grant
          Journey a limited license to process that content for the sole purpose of
          providing the Service to you, as described in the{" "}
          <a href="/privacy" className="text-cyan-300 hover:text-cyan-200">
            Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section id="ai" title="AI output disclaimer">
        <p>
          Journey uses large language models to generate responses, summaries, and
          memory extractions. AI output can be inaccurate, incomplete, or out of
          date. Do not rely on Journey for legal, medical, financial, safety-critical,
          or other professional advice. Verify any consequential information
          independently.
        </p>
      </Section>

      <Section id="availability" title="Availability &amp; modifications">
        <p>
          The Service is provided on an as-available basis. We may modify, suspend,
          or discontinue features at any time without notice. We will make a
          reasonable effort to preserve owner data during planned changes.
        </p>
      </Section>

      <Section id="termination" title="Termination">
        <p>
          You may stop using the Service at any time. We may suspend or terminate
          access if these Terms are violated. Upon termination, owner data may be
          deleted after a short grace period unless retention is required by law.
        </p>
      </Section>

      <Section id="warranty" title="No warranty">
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
          ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
      </Section>

      <Section id="liability" title="Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, JOURNEY AND ITS OPERATORS WILL NOT
          BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE,
          ARISING OUT OF OR IN CONNECTION WITH THE SERVICE.
        </p>
      </Section>

      <Section id="indemnity" title="Indemnification">
        <p>
          You agree to indemnify and hold harmless Journey and its operators from
          any claim, demand, or liability arising out of your use of the Service or
          your violation of these Terms — including any claims arising from messages
          you sent via the SMS integration to recipients without proper consent.
        </p>
      </Section>

      <Section id="law" title="Governing law">
        <p>
          These Terms are governed by the laws of the jurisdiction in which the
          Service is operated, without regard to conflict-of-law principles. Any
          disputes will be resolved in the appropriate courts of that jurisdiction.
        </p>
      </Section>

      <Section id="changes" title="Changes to these Terms">
        <p>
          We may update these Terms occasionally. Material changes will be reflected
          here with an updated date. Continued use of the Service after a change
          constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section id="contact" title="Contact">
        <p>
          For any question about these Terms, contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </div>
  );
}
